import { cn } from '../../lib/cn';
import type { FeedbackKind } from '../../motionxp/framework';

type Props = {
  kind: FeedbackKind;
  title: string;
  message?: string;
  onDismiss?: () => void;
  action?: { label: string; onClick: () => void };
};

const styles: Record<FeedbackKind, string> = {
  success: 'border-success-200 bg-success-50 text-success-800 dark:border-success-800 dark:bg-success-950 dark:text-success-200',
  warning: 'border-warning-200 bg-warning-50 text-warning-800 dark:border-warning-800 dark:bg-warning-950 dark:text-warning-200',
  error: 'border-error-200 bg-error-50 text-error-800 dark:border-error-800 dark:bg-error-950 dark:text-error-200',
  info: 'border-info-200 bg-info-50 text-info-800 dark:border-info-800 dark:bg-info-950 dark:text-info-200',
  progress: 'border-brand-200 bg-brand-50 text-brand-800 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-200',
};

const icons: Record<FeedbackKind, string> = {
  success: '✓',
  warning: '⚠',
  error: '✕',
  info: 'ℹ',
  progress: '↻',
};

export function FeedbackToast({ kind, title, message, onDismiss, action }: Props) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3 shadow-md transition-all duration-200 ease-out',
        styles[kind],
      )}
    >
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/60 text-xs font-bold dark:bg-black/20" aria-hidden="true">
        {icons[kind]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        {message && <p className="mt-0.5 text-xs opacity-90">{message}</p>}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-2 text-xs font-semibold underline underline-offset-2 hover:no-underline"
          >
            {action.label}
          </button>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="shrink-0 rounded p-1 opacity-70 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}
